import React, { useEffect, useState, useCallback } from "react";
import { Table, Input, Select, Tooltip, Form, Modal, message } from "antd";
import { CopyOutlined, DeleteOutlined, EditOutlined, HistoryOutlined, SearchOutlined } from "@ant-design/icons";
import styled from "styled-components";
import moment from "moment";
import { connect } from "react-redux";
import { getUserInfo } from "redux/selectors";
import { CALL_FEATCH_API } from "common/API";
import TaskFilterModal from "pages/TaskFilterModal";
import AddTask from "organisms/AddTask";
import EditTask from "organisms/EditTask";
import { Button } from "antd";

const { Option } = Select;
const PAGE_LIMIT = 10;
const PRIMARY = "#10b981";

const Wrap = styled.div`
  .filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
    gap: 10px;
  }
  .total-count { font-weight: 600; font-size: 16px; color: #111827; }
  .filters {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    flex-wrap: nowrap;
  }
  .filter-input.ant-input-affix-wrapper {
    border-radius: 20px !important;
    height: 38px !important;
    min-width: 180px;
    max-width: 220px;
    display: flex;
    align-items: center;
    padding: 0 11px;
    border: 1px solid #d9d9d9;
    box-shadow: none !important;
  }
  .filter-input.ant-input-affix-wrapper .ant-input { border: none !important; box-shadow: none !important; }
  .filter-input.ant-input-affix-wrapper:hover,
  .filter-input.ant-input-affix-wrapper:focus-within { border-color: ${PRIMARY} !important; box-shadow: none !important; }
  .filter-input .ant-input-prefix { margin-right: 6px; }
  .filter-select { min-width: 140px; max-width: 180px; }
  .filter-select .ant-select-selector {
    border-radius: 20px !important;
    height: 38px !important;
    display: flex;
    align-items: center;
    padding: 0 14px !important;
    border: 1px solid #d9d9d9 !important;
    box-shadow: none !important;
  }
  .filter-select.ant-select-focused .ant-select-selector,
  .filter-select .ant-select-selector:hover { border-color: ${PRIMARY} !important; box-shadow: none !important; }
  .filter-select .ant-select-selection-placeholder,
  .filter-select .ant-select-selection-item { line-height: 36px !important; }
  .filter-select .ant-select-arrow { color: #6b7280; }
  .ant-table { border-radius: 8px; }
  .action-icons {
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.ant-table-tbody > tr:hover .action-icons {
  opacity: 1;
}
`;

const StyledModal = styled(Modal)`
  .ant-modal-content { border-radius: 12px; }
  .ant-modal-header { border-radius: 12px 12px 0 0; }
`;

const ActionIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #deefee;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #00685f;
  transition: all 0.2s ease;
  &:hover { background: #deefee; }
`;

const StatusBtn = styled.div`
  height: 32px;
  border-radius: 8px;
  background: #deefee;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #00685f;
  transition: all 0.2s ease;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  &:hover { background: #deefee; }
`;

const CancelBtn = styled(Button)`
  && {
    height: 38px; padding: 0 24px; border-radius: 8px;
    border: 1px solid #000000; color: #000000; font-weight: 500; background: #fff;
  }
  &&:hover { border-color: #000000; color: #111827; background: #f9fafb; }
`;

const ConfirmBtn = styled(Button)`
  && {
    height: 38px; padding: 0 28px; border-radius: 8px;
    background-color: #00685f !important; border: none; color: #ffffff; font-weight: 600;
  }
  &&:hover, &&:focus { background-color: #00685f !important; color: #ffffff !important; border: none; }
`;

function ClientTaskList({ clientId, userInfo }) {
  const [taskList, setTaskList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [filterPriority, setFilterPriority] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterAssignee, setFilterAssignee] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(null);

  // ── action states (mirrored from AllTasks) ──
  const [editingTask, setEditingTask] = useState(false);
  const [taskDetails, setTaskDetails] = useState({});
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [historyModal, setHistoryModal] = useState(false);
  const [historyRecord, setHistoryRecord] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [cloningTask, setCloningTask] = useState(false);
  const [cloneDetails, setCloneDetails] = useState({});
  const [statusModal, setStatusModal] = useState(false);
  const [statusRecord, setStatusRecord] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [viewModal, setViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [matterList, setMatterList] = useState([]);

  const handleFilterApply = (filters) => setAppliedFilters(filters);
  const handleFilterClear = () => setAppliedFilters(null);

  const taskTypeOptions = [...new Set(taskList.map((t) => t.tasktype).filter(Boolean))];
  const createdByOptions = [...new Set(taskList.map((t) => t.createdby).filter(Boolean))];

  // ── Fetch status options (from AllTasks) ───────────────────────────────
  useEffect(() => {
    const fetchStatuses = async () => {
      const res = await CALL_FEATCH_API(
        `insyts-task/gettaskstatus?userid=${userInfo?.userid}&page=1&limit=500`,
        "GET"
      );
      if (res?.response?.code === "SUCCESS") {
        setStatusOptions(res.response.data.map((s) => ({ label: s.statusname, value: s.statusname })));
      }
    };
    if (userInfo?.userid) fetchStatuses();
  }, [userInfo?.userid]);

  // ── Fetch matters for this client ───────────────────────────────────────
  // NOTE: confirm the actual endpoint + response field names against
  // whatever API your Matters page / AddTask component already uses.
  useEffect(() => {
    const fetchMatters = async () => {
      try {
        const res = await CALL_FEATCH_API(
          `insyts-matter/getmatter?clientid=${clientId}&userid=${userInfo?.userid}&managingoffice=${encodeURIComponent(userInfo.managingoffice || "")}&page=1&limit=500`,
          "GET"
        );
        // this API returns { status: true, message, data, pagination } — no "code" field
        if (res?.response?.status === true || res?.status === true) {
          const raw = res?.response?.data ?? res?.data ?? [];
          const matters = raw
            .filter((m) => m.clientId === clientId) // belt-and-suspenders, in case API isn't filtering server-side
            .map((m) => ({
              matterid: m.matterId,
              mattername: m.jobTitle,
            }));
          setMatterList(matters);
        }
      } catch (e) {
        console.error("[ClientTaskList] fetchMatters error -->", e);
      }
    };
    if (clientId && userInfo?.userid) fetchMatters();
  }, [clientId, userInfo?.userid]);

  // ── Fetch task history (from AllTasks) ─────────────────────────────────
  const fetchTaskHistory = async (record) => {
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const res = await CALL_FEATCH_API(
        `insyts-task/gettaskhistory?taskid=${record.taskid}&userid=${userInfo?.userid}`,
        "GET"
      );
      if (res?.response?.code === "SUCCESS") {
        setHistoryData(res.response.data || []);
      }
    } catch (_) { }
    setHistoryLoading(false);
  };

  // ── Action handlers (from AllTasks) ────────────────────────────────────
  const handleEdit = (e, record) => {
    e.stopPropagation();
    setTaskDetails(record);
    setEditingTask(true);
  };

  const handleDelete = (e, record) => {
    e.stopPropagation();
    setDeleteRecord(record);
    setDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    const response = await CALL_FEATCH_API(`insyts-task/deletetask`, "post", {
      taskid: deleteRecord.taskid,
      userid: userInfo?.userid,
      deletedby: userInfo?.username || userInfo?.userid || "",
    });
    if (response?.code === "SUCCESS" || response?.response?.code === "SUCCESS") {
      message.success("Task deleted successfully.");
      getTaskList(currentPage);
    } else {
      message.error("Failed to delete task. Please try again.");
    }
    setDeleteModal(false);
    setDeleteRecord(null);
  };

  const handleStatusUpdate = async () => {
    const response = await CALL_FEATCH_API("insyts-task/edittask", "post", {
      taskid: statusRecord.taskid,
      userid: userInfo?.userid,
      userName: userInfo?.name || null,
      taskstatus: newStatus,
      updatedby: userInfo?.userid || "",
      statusOnly: true,
    });
    if (response?.code === "SUCCESS" || response?.response?.code === "SUCCESS") {
      message.success("Status updated successfully.");
      getTaskList(currentPage);
    } else {
      message.error("Failed to update status.");
    }
    setStatusModal(false);
    setStatusRecord(null);
    setNewStatus(null);
  };

  const getTaskList = useCallback(async (page = 1) => {
    if (!clientId || !userInfo?.userid) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        clientid: clientId,
        page: String(page),
        limit: String(PAGE_LIMIT),
      });

      if (filterStatus) params.append("taskstatus", filterStatus);
      if (filterAssignee) params.append("assignedtoname", filterAssignee);
      if (filterPriority) params.append("priority", filterPriority);

      if (appliedFilters) {
        if (appliedFilters.tasktype) params.append("tasktype", appliedFilters.tasktype);
        if (appliedFilters.matterid) params.append("matterid", appliedFilters.matterid);
        if (appliedFilters.createdby) params.append("createdby", appliedFilters.createdby);
        if (appliedFilters.isprivate !== null && appliedFilters.isprivate !== undefined)
          params.append("isprivate", appliedFilters.isprivate);
        if (appliedFilters.dueDateFrom)
          params.append("duedatefrom", moment(appliedFilters.dueDateFrom).format("YYYY-MM-DD"));
        if (appliedFilters.dueDateTo)
          params.append("duedateto", moment(appliedFilters.dueDateTo).format("YYYY-MM-DD"));
        if (appliedFilters.createdDateFrom)
          params.append("createddatefrom", moment(appliedFilters.createdDateFrom).format("YYYY-MM-DD"));
        if (appliedFilters.createdDateTo)
          params.append("createddateto", moment(appliedFilters.createdDateTo).format("YYYY-MM-DD"));
      }

      const res = await CALL_FEATCH_API(`insyts-task/gettask?${params.toString()}`, "GET");
      if (res?.response?.code === "SUCCESS") {
        setTaskList(res.response.data || []);
        setTotalCount(
          res.response.total ?? res.response.pagination?.total ?? res.response.data?.length ?? 0
        );
        setCurrentPage(page);
      }
    } catch (e) {
      console.error("[ClientTaskList] error -->", e);
    }
    setLoading(false);
  }, [clientId, userInfo?.userid, filterStatus, filterAssignee, filterPriority, appliedFilters]);

  useEffect(() => {
    setCurrentPage(1);
    getTaskList(1);
  }, [clientId, userInfo?.userid, filterStatus, filterAssignee, filterPriority, appliedFilters]);

  const displayData = taskList.filter((item) =>
    !searchText || item.taskname?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: "Created by",
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 13 }}>{record.createdbyname || "N/A"}</div>
          <small style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic" }}>
            {record.createdat ? moment(record.createdat).format("Do MMM, YYYY") : ""}
          </small>
        </div>
      ),
    },
    { title: "Task title", dataIndex: "taskname" },
    { title: "Assigned to", dataIndex: "assignedtoname" },
    { title: "Priority", dataIndex: "priority" },
    { title: "Status", dataIndex: "taskstatus" },
    { title: "Type", dataIndex: "tasktype" },
    { title: "Matter", dataIndex: "mattername" },
    {
      title: "Action",
      key: "action",
      width: 220,
      render: (_, record) => {
        const isCreator = record.createdby === userInfo?.userid;
        const isAssignee = record.assignedto === userInfo?.userid;
        return (
          <div className="action-icons">
            {(isCreator || isAssignee) && (
              <Tooltip title="History">
                <ActionIcon onClick={(e) => { e.stopPropagation(); setHistoryRecord(record); setHistoryModal(true); fetchTaskHistory(record); }}>
                  <HistoryOutlined />
                </ActionIcon>
              </Tooltip>
            )}
            {(isCreator || isAssignee) && (
              <Tooltip title="Clone">
                <ActionIcon onClick={(e) => { e.stopPropagation(); setCloneDetails(record); setCloningTask(true); }}>
                  <CopyOutlined />
                </ActionIcon>
              </Tooltip>
            )}
            {isCreator && (
              <Tooltip title="Edit">
                <ActionIcon onClick={(e) => handleEdit(e, record)}>
                  <EditOutlined />
                </ActionIcon>
              </Tooltip>
            )}
            {isCreator && (
              <Tooltip title="Delete">
                <ActionIcon onClick={(e) => handleDelete(e, record)}>
                  <DeleteOutlined />
                </ActionIcon>
              </Tooltip>
            )}
            {isAssignee && (
              <Tooltip title="Update Status">
                <StatusBtn onClick={(e) => { e.stopPropagation(); setStatusRecord(record); setNewStatus(record.taskstatus); setStatusModal(true); }}>
                  Update Status
                </StatusBtn>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Wrap>
      <div className="filters-header">
        <div className="total-count">Total Tasks: {totalCount}</div>
        <div className="filters">
          <Input
            placeholder="Search"
            prefix={<SearchOutlined />}
            className="filter-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
          <Select placeholder="Status" className="filter-select" value={filterStatus || undefined}
            onChange={(val) => setFilterStatus(val ?? null)} allowClear>
            {[...new Set(taskList.map((t) => t.taskstatus).filter(Boolean))].map((s) => (
              <Option key={s} value={s}>{s}</Option>
            ))}
          </Select>
          <Select placeholder="Assignee" className="filter-select" value={filterAssignee || undefined}
            onChange={(val) => setFilterAssignee(val ?? null)} allowClear showSearch optionFilterProp="children">
            {[...new Set(taskList.map((t) => t.assignedtoname).filter(Boolean))].map((a) => (
              <Option key={a} value={a}>{a}</Option>
            ))}
          </Select>
          <Select placeholder="Priority" className="filter-select" value={filterPriority || undefined}
            onChange={(val) => setFilterPriority(val ?? null)} allowClear>
            {["High", "Medium", "Low"].map((p) => (
              <Option key={p} value={p}>{p}</Option>
            ))}
          </Select>
          <TaskFilterModal
            taskTypeOptions={taskTypeOptions}
            matterOptions={matterList}
            createdByOptions={createdByOptions}
            onApply={handleFilterApply}
            onClear={handleFilterClear}
          />
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={displayData}
        loading={loading}
        rowKey="taskid"
        size="small"
        onRow={(record) => ({
          onClick: () => { setViewRecord(record); setViewModal(true); },
          style: { cursor: "pointer" },
        })}
        pagination={{
          current: currentPage,
          pageSize: PAGE_LIMIT,
          total: totalCount,
          onChange: (page) => getTaskList(page),
          showSizeChanger: false,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tasks`,
        }}
      />

      {/* Delete Modal */}
      <StyledModal visible={deleteModal}
        onCancel={() => { setDeleteModal(false); setDeleteRecord(null); }}
        footer={null} width={420} closable={false} centered destroyOnClose>
        <div style={{ textAlign: "center", padding: "24px 16px 8px" }}>
          <p style={{ fontSize: 15, fontWeight: 500, color: "#111827", marginBottom: 32 }}>
            Are you sure you want to delete this Task?
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
            <CancelBtn onClick={() => { setDeleteModal(false); setDeleteRecord(null); }} style={{ width: 100 }}>No</CancelBtn>
            <ConfirmBtn onClick={handleDeleteConfirm} style={{ width: 100 }}>Yes</ConfirmBtn>
          </div>
        </div>
      </StyledModal>

      {/* Edit Modal */}
      <EditTask
        isModalVisible={editingTask}
        closeModal={(success) => {
          setEditingTask(false);
          if (success) getTaskList(currentPage);
        }}
        getTaskList={() => getTaskList(currentPage)}
        taskDetails={taskDetails}
      />

      {/* Clone Modal */}
      <AddTask
        isModalVisible={cloningTask}
        closeModal={() => { setCloningTask(false); setCloneDetails({}); }}
        getTaskList={() => getTaskList(currentPage)}
        isClone={true}
        cloneData={cloneDetails}
        matterList={matterList}
      />

      {/* History Modal */}
      <StyledModal visible={historyModal}
        onCancel={() => { setHistoryModal(false); setHistoryRecord(null); setHistoryData([]); }}
        footer={null} width={600} title="Task History" destroyOnClose>
        <div style={{ border: "1px solid #f0f0f0", borderRadius: 10, overflow: "hidden" }}>
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", background: "#f9fafb",
            padding: "12px 20px", fontWeight: 600, fontSize: 14, color: "#374151", borderBottom: "1px solid #f0f0f0"
          }}>
            <span>Action by</span><span>Date &amp; Time</span>
          </div>
          {historyLoading ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#888" }}>Loading...</div>
          ) : historyData.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#888", fontSize: 14 }}>No history available.</div>
          ) : (
            historyData.map((h, i) => (
              <div key={i} style={{
                display: "grid", gridTemplateColumns: "1fr 1fr", padding: "16px 20px",
                borderBottom: i < historyData.length - 1 ? "1px solid #f5f5f5" : "none", fontSize: 14, color: "#6b7280", background: "#fff"
              }}>
                <span>
                  {h.action === "Updated status by" ? "Status Updated by" : h.action}{" "}
                  <strong style={{ color: "#111827" }}>{h.actionbyname || "N/A"}</strong>
                </span>
                <span>{h.actionat ? moment(h.actionat).format("Do MMM YYYY; hh:mm A") : "—"}</span>
              </div>
            ))
          )}
        </div>
      </StyledModal>

      {/* View Modal */}
      <StyledModal
        visible={viewModal}
        onCancel={() => { setViewModal(false); setViewRecord(null); }}
        footer={null} width={520} destroyOnClose
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 24 }}>
            <span style={{ fontWeight: 700, fontSize: 16 }}>Task Details</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginRight: 15 }}>
              {(viewRecord?.createdby === userInfo?.userid || viewRecord?.assignedto === userInfo?.userid) && (
                <Tooltip title="History">
                  <ActionIcon onClick={(e) => { e.stopPropagation(); setHistoryRecord(viewRecord); setHistoryModal(true); fetchTaskHistory(viewRecord); setViewModal(false); }}>
                    <HistoryOutlined />
                  </ActionIcon>
                </Tooltip>
              )}
              {(viewRecord?.createdby === userInfo?.userid || viewRecord?.assignedto === userInfo?.userid) && (
                <Tooltip title="Clone">
                  <ActionIcon onClick={(e) => { e.stopPropagation(); setCloneDetails(viewRecord); setCloningTask(true); setViewModal(false); }}>
                    <CopyOutlined />
                  </ActionIcon>
                </Tooltip>
              )}
              {viewRecord?.createdby === userInfo?.userid && (
                <Tooltip title="Edit">
                  <ActionIcon onClick={(e) => { e.stopPropagation(); setTaskDetails(viewRecord); setEditingTask(true); setViewModal(false); }}>
                    <EditOutlined />
                  </ActionIcon>
                </Tooltip>
              )}
              {viewRecord?.createdby === userInfo?.userid && (
                <Tooltip title="Delete">
                  <ActionIcon onClick={(e) => { e.stopPropagation(); setDeleteRecord(viewRecord); setDeleteModal(true); setViewModal(false); }}>
                    <DeleteOutlined />
                  </ActionIcon>
                </Tooltip>
              )}
              {viewRecord?.assignedto === userInfo?.userid && (
                <ActionIcon
                  style={{ width: "auto", padding: "0 12px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}
                  onClick={(e) => { e.stopPropagation(); setStatusRecord(viewRecord); setNewStatus(viewRecord.taskstatus); setStatusModal(true); setViewModal(false); }}
                >
                  Update Status
                </ActionIcon>
              )}
            </div>
          </div>
        }
      >
        {viewRecord && (
          <div style={{ fontSize: 13 }}>
            {[
              { label: "Name of the Task", value: viewRecord.taskname },
              { label: "Priority", value: viewRecord.priority },
              { label: "Task Status", value: viewRecord.taskstatus },
              { label: "Created by", value: `${viewRecord.createdbyname || ""}${viewRecord.createdat ? "; " + moment(viewRecord.createdat).format("Do MMM YYYY") : ""}` },
              { label: "Assigned to", value: viewRecord.assignedtoname },
              { label: "Due Date & Time", value: `${viewRecord.duedate ? moment(viewRecord.duedate).format("Do MMM YYYY") : ""}${viewRecord.duetime ? "; " + moment(viewRecord.duetime, "HH:mm").format("hh:mm A") : ""}` },
              { label: "Task Type", value: viewRecord.tasktype },
              { label: "Matter", value: viewRecord.mattername },
              { label: "Estimated Time of Completion", value: viewRecord.estimatedtime },
            ].map(({ label, value }) =>
              value ? (
                <div key={label} style={{ display: "flex", padding: "10px 0" }}>
                  <span style={{ width: 200, color: "#6b7280", flexShrink: 0 }}>{label}</span>
                  <span style={{ color: "#111827", fontWeight: 500 }}>{value}</span>
                </div>
              ) : null
            )}
            {viewRecord.description && (
              <div style={{ paddingTop: 14 }}>
                <div style={{ color: "#6b7280", marginBottom: 6 }}>Task Description</div>
                <div style={{ color: "#111827", lineHeight: 1.7 }}>{viewRecord.description}</div>
              </div>
            )}
          </div>
        )}
      </StyledModal>

      {/* Status Modal */}
      <StyledModal visible={statusModal}
        onCancel={() => { setStatusModal(false); setStatusRecord(null); setNewStatus(null); }}
        footer={null} width={480} title="Update Status" destroyOnClose centered>
        <div style={{ padding: "16px 0" }}>
          <Select style={{ width: "100%" }} value={newStatus} onChange={(val) => setNewStatus(val)}
            getPopupContainer={(trigger) => trigger.parentElement}>
            {statusOptions.map((s) => <Option key={s.value} value={s.value}>{s.label}</Option>)}
          </Select>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 32 }}>
            <CancelBtn onClick={() => { setStatusModal(false); setStatusRecord(null); setNewStatus(null); }}>Cancel</CancelBtn>
            <ConfirmBtn onClick={handleStatusUpdate}>Update</ConfirmBtn>
          </div>
        </div>
      </StyledModal>
    </Wrap>
  );
}

const mapStateToProps = (state) => ({ userInfo: getUserInfo(state) });
export default connect(mapStateToProps, null)(ClientTaskList);